import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import { useDashboard } from '../../../context/DashboardContext';
import {isSuppressedItem, isSuppressedBenchmark} from '../../DashboardCommon/dashboardCards';
import {SuppressedValue} from '../../DashboardCommon/SuppressedValue.comp';
import { READINESS_BAND_COLORS, RISK_BAND_COLORS } from '../constants';

import {findCard, benchmarkOf} from '../../DashboardCommon/dashboardCards';
import {BandChip} from '../../DashboardCommon/widgets.comp';
import {UNIT, fmtValue} from '../../DashboardCommon/units';
const NAVY   = '#052F44';
const ORANGE = '#FF6900';
const TEAL   = '#1B6B55';

const getReadinessColor = (band) => {
    if (band && READINESS_BAND_COLORS[band]) return READINESS_BAND_COLORS[band];
    if (band) {
        const key = Object.keys(READINESS_BAND_COLORS).find(k => k.toLowerCase() === band.toLowerCase());
        if (key) return READINESS_BAND_COLORS[key];
    }
    return undefined;
};

/* ── Tier badge ─────────────────────────────────────────────────── */
const TierBadge = ({ label, color }) => {
    if (!label) return null;
    return (
        <Box sx={{
            backgroundColor: color,
            color: '#ffffff',
            fontSize: '14px',
            fontWeight: 700,
            px: 3,
            py: 0.8,
            borderRadius: '8px',
            textAlign: 'center',
            minWidth: '130px',
        }}>
            {label}
        </Box>
    );
};

/* ── Stacked risk bar: this client's risk indicators by band, with the counts on the
      segments and the total at the end (QA 9 Oct, LA-PD-1: "missing numbers for these bars") ── */
const RiskBar = ({ counts, rubrics }) => {
    const total = rubrics.reduce((sum, r) => sum + (counts[r] || 0), 0);
    if (total === 0) {
        return (
            <Box sx={{display: 'flex', height: '18px', borderRadius: '4px', overflow: 'hidden', mt: 0.8, backgroundColor: 'var(--brand-surface-subtle, #E5E7EB)'}}/>
        );
    }
    return (
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mt: 0.8}}>
            <Box sx={{display: 'flex', flex: 1, height: '18px', borderRadius: '4px', overflow: 'hidden'}}>
                {rubrics.map((rubric) => {
                    const val = counts[rubric] || 0;
                    if (val <= 0) return null;
                    const share = val / total;
                    return (
                        <Tooltip key={rubric} title={`${rubric}: ${val} of ${total} risk indicators`} arrow placement="top">
                            <Box sx={{
                                flex: val, minWidth: 0, backgroundColor: RISK_BAND_COLORS[rubric],
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: '#fff', fontSize: '11px', fontWeight: 700, cursor: 'default',
                            }}>
                                {share >= 0.1 ? val : ''}
                            </Box>
                        </Tooltip>
                    );
                })}
            </Box>
            <Typography sx={{fontSize: '12px', fontWeight: 700, color: NAVY, width: 28, textAlign: 'right', flexShrink: 0}}>{total}</Typography>
        </Box>
    );
};

const BY_COUNTRY_SECTION_CODE = 'S_7';
// The risk profile section lends the reference under the band bars: the Global avg of "Any Cyber Risk" (PD·S_15a·C_3).
const RISK_PROFILE_SECTION_CODE = 'S_15a';
const CARD_SECTIONS = [BY_COUNTRY_SECTION_CODE, RISK_PROFILE_SECTION_CODE];

const ByCountryTab = () => {
    const { dashboardData, loading } = useDashboard({ card_sections: CARD_SECTIONS });
    const [selected, setSelected] = useState('');

    // 1. Safely locate the S_7 section
    const s7Section = dashboardData?.cards?.sections?.find(
        (section) => section.section_code === BY_COUNTRY_SECTION_CODE
    );
    const titleCard = s7Section?.cards?.find(card => card.card_type === 'TitleCard');

    const cards = s7Section?.cards || [];
    const c2 = cards.find(c => c.card_code === 'C_2');
    const c3 = cards.find(c => c.card_code === 'C_3');
    const c4 = cards.find(c => c.card_code === 'C_4');

    const countriesList = useMemo(() => {
        return c2?.list_objects || [];
    }, [c2]);

    useEffect(() => {
        const codes = countriesList.map(o => o.code);
        if (codes.length > 0 && (!selected || !codes.includes(selected))) {
            setSelected(codes[0]);
        }
    }, [countriesList, selected]);

    const selectedCountryObj = useMemo(() => {
        return countriesList.find(c => c.code === selected);
    }, [countriesList, selected]);

    const c3Item = useMemo(() => {
        if (!c3 || !selected) return null;
        return c3.items?.find(it => it.label === selected || it.dims?.country === selected);
    }, [c3, selected]);

    const rubrics = useMemo(() => {
        return c4?.table_rubrics || [];
    }, [c4]);

    const clientsData = useMemo(() => {
        if (!c4 || !selected) return [];
        const tableObjects = c4.table_objects || [];
        // PD.S_7.C_4 sends the client column as "Network" (the page only
        // looked for "Partner", so this block was always empty).
        const NAME_COLS = ['network', 'partner', 'client', 'school'];
        const countryCol = tableObjects.find(col => col.column_name?.toLowerCase() === 'country');
        const partnerCol = tableObjects.find(col => NAME_COLS.includes(col.column_name?.toLowerCase()));
        if (!countryCol || !partnerCol) return [];

        const countryValues = countryCol.values || [];
        const partnerValues = partnerCol.values || [];

        // Risk columns — one per risk name; the card sends "Any Cyber Risk"
        // twice (expe_CPCI_CyberRisks and expe_CyberRisks), which would count
        // the same risk two times in the band share.
        const seen = new Set();
        const riskCols = tableObjects.filter(col => {
            const name = col.column_name?.toLowerCase();
            if (name === 'country' || col === partnerCol || seen.has(name)) return false;
            seen.add(name);
            return true;
        });

        const clients = [];
        for (let i = 0; i < countryValues.length; i++) {
            const matchSelected = countryValues[i] === selected || (selectedCountryObj && countryValues[i] === selectedCountryObj.name);
            if (matchSelected) {
                const partnerName = partnerValues[i] || '';
                const counts = {};
                rubrics.forEach(r => { counts[r] = 0; });
                
                riskCols.forEach(col => {
                    const val = col.values?.[i];
                    if (val && rubrics.includes(val)) {
                        counts[val]++;
                    }
                });

                clients.push({
                    name: partnerName,
                    counts
                });
            }
        }
        return clients;
    }, [c4, selected, rubrics, selectedCountryObj]);

    if (loading) {
        return <CircularLoader minHeight="200px" />;
    }

    if (!s7Section) {
        return (
            <NoDataAvailable />
        );
    }

    // A country under the n floor arrives flagged: placeholder, no band. A value that is not sent reads "—", never 0.
    const countrySuppressed = isSuppressedItem(c3Item);
    const countryScoreRaw = c3Item?.value !== undefined && c3Item?.value !== null ? c3Item.value : c3Item?.data?.value;
    const countryScore = !countrySuppressed && countryScoreRaw !== undefined && countryScoreRaw !== null ? Math.round(countryScoreRaw) : null;
    const countryBand = countrySuppressed ? null : (c3Item?.band || c3Item?.data?.band);
    const countryColor = getReadinessColor(countryBand);

    const benchmarkValues = (c3Item?.benchmark_values || []).filter(bv => {
        const name = (bv.name || '').toLowerCase();
        const token = (bv.data?.token || '').toLowerCase();
        return name !== 'coverage' && token !== 'coverage';
    });

    // Mockup title (PD slide 47) when the card sends none.
    const pageTitle = titleCard?.title || 'Portfolio Baseline';
    // Bug sheet Ian-PD (Title is missing, 24 Sep): the page subtitle — each tab
    // asks its own question (P1 Partner Dashboard deck, slide 14: By Country).
    const pageSubtitle = titleCard?.subtitle || titleCard?.footer || 'Which countries report low child online safety readiness and high risk experiences?';
    const c3Title = c3?.title;
    const c4Title = c4?.title;

    // Reference for the band bars (QA LA-PD-1: "there is no benchmark"): the Global avg of Any Cyber Risk and its band.
    const anyRiskItem = (findCard(dashboardData, RISK_PROFILE_SECTION_CODE, 'C_3')?.items || []).find((it) => /any cyber risk/i.test(it.label || ''));
    const globalRisk = anyRiskItem ? benchmarkOf(anyRiskItem, 'Global avg.') : null;
    const globalRiskBand = anyRiskItem?.benchmark_values?.find((b) => /global/i.test(b.name || ''))?.band || null;

    return (
        <Box sx={{ mt: 1.5 }}>
            {/* Title */}
            <Typography sx={{fontSize: '23px', fontWeight: 800, color: NAVY, mb: 0.5}}>
                {pageTitle}
            </Typography>

            {/* Subtitle */}
            <Typography sx={{fontSize: '14px', fontStyle: 'italic', color: '#4B5563', mb: 3}}>
                {pageSubtitle}
            </Typography>

            <Box sx={{display: 'flex', gap: 2.5, alignItems: 'stretch'}}>

                {/* ── Left: country list ── */}
                <Box sx={{
                    border: '1px solid var(--brand-border, #E5E7EB)',
                    borderRadius: '12px',
                    backgroundColor: 'var(--brand-surface, #ffffff)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    p: 2.5,
                    minWidth: '280px',
                    flexShrink: 0,
                }}>
                    <Typography sx={{
                        fontSize: '11px',
                        fontWeight: 700,
                        color: '#6A7282',
                        letterSpacing: '1px',
                        textTransform: 'uppercase',
                        mb: 1.5,
                    }}>
                        Country
                    </Typography>
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
                        {countriesList.map((c) => {
                            const active = c.code === selected;
                            return (
                                <Box
                                    key={c.code}
                                    onClick={() => setSelected(c.code)}
                                    sx={{
                                        px: 1.5,
                                        py: 1,
                                        borderRadius: '8px',
                                        borderLeft: active ? `3px solid ${TEAL}` : '3px solid transparent',
                                        backgroundColor: active ? 'var(--brand-surface-subtle, #F0F7F4)' : 'transparent',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: active ? 700 : 400,
                                        color: active ? TEAL : '#374151',
                                        '&:hover': {backgroundColor: 'var(--brand-surface-subtle, #F9FAFB)'},
                                        transition: 'all 0.15s',
                                    }}
                                >
                                    {c.name}
                                </Box>
                            );
                        })}
                    </Box>
                </Box>

                {/* ── Middle: composite scores ── */}
                <Box sx={{
                    border: '1px solid var(--brand-border, #E5E7EB)',
                    borderRadius: '12px',
                    backgroundColor: 'var(--brand-surface, #ffffff)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    p: 3,
                    flex: '0 0 300px',
                }}>
                    <Typography sx={{fontSize: '18px', fontWeight: 700, color: NAVY, mb: 2.5}}>
                        {c3Title}
                    </Typography>

                    {/* Country score */}
                    <Typography sx={{fontSize: '11px', fontWeight: 700, color: '#6A7282', letterSpacing: '1px', textTransform: 'uppercase', mb: 1}}>
                        {selectedCountryObj?.name || selected}
                    </Typography>
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                        {countrySuppressed ? <SuppressedValue/> : (
                            <Typography sx={{fontSize: '52px', fontWeight: 800, color: NAVY, lineHeight: 1}}>
                                {countryScore ?? '—'}
                            </Typography>
                        )}
                        {countryBand && <TierBadge label={countryBand} color={countryColor}/>}
                    </Box>

                    {/* Dynamic Benchmark Values */}
                    {benchmarkValues.map((bv, idx) => {
                        const name = (bv.name || '').toUpperCase();
                        const bvSuppressed = isSuppressedBenchmark(bv);          // a comparator under the n floor
                        const valRaw = bv.value !== undefined && bv.value !== null ? bv.value : bv.data?.value;
                        const val = !bvSuppressed && valRaw !== undefined && valRaw !== null ? Math.round(valRaw) : null;
                        const band = bvSuppressed ? null : (bv.band || bv.data?.band);
                        const color = getReadinessColor(band, valRaw);

                        return (
                            <React.Fragment key={bv.name || idx}>
                                <Box sx={{borderTop: '1px solid #F3F4F6', mb: 2.5}}/>
                                <Typography sx={{fontSize: '11px', fontWeight: 700, color: '#6A7282', letterSpacing: '1px', textTransform: 'uppercase', mb: 1}}>
                                    {name}
                                </Typography>
                                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                                    {bvSuppressed ? <SuppressedValue/> : (
                                        <Typography sx={{fontSize: '52px', fontWeight: 800, color: NAVY, lineHeight: 1}}>
                                            {val ?? '—'}
                                        </Typography>
                                    )}
                                    {band && <TierBadge label={band} color={color}/>}
                                </Box>
                            </React.Fragment>
                        );
                    })}
                </Box>

                {/* ── Right: cyber risk bars by client ── */}
                <Box sx={{
                    border: '1px solid var(--brand-border, #E5E7EB)',
                    borderRadius: '12px',
                    backgroundColor: 'var(--brand-surface, #ffffff)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    p: 3,
                    flex: 1,
                }}>
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 1}}>
                        <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY}}>
                            {c4Title}
                        </Typography>

                        {/* Legend */}
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap'}}>
                            {rubrics.map((rubric) => {
                                const color = RISK_BAND_COLORS[rubric];
                                return (
                                    <Box key={rubric} sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                        <Box sx={{width: 12, height: 12, borderRadius: '2px', backgroundColor: color}}/>
                                        <Typography sx={{fontSize: '11px', color: '#6A7282'}}>{rubric}</Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>

                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2.5}}>
                        <Typography sx={{fontSize: '12px', color: '#6A7282'}}>Each client's risk indicators by band — the figures are counts; the total sits at the end of the bar.</Typography>
                        {globalRisk !== null && (
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                <Typography sx={{fontSize: '12px', color: '#6A7282'}}>Reference · Global avg, Any Cyber Risk</Typography>
                                <Typography sx={{fontSize: '13px', fontWeight: 700, color: NAVY}}>{fmtValue(globalRisk, UNIT.PERCENT)}</Typography>
                                {globalRiskBand && <BandChip label={globalRiskBand} color={RISK_BAND_COLORS[globalRiskBand]}/>}
                            </Box>
                        )}
                    </Box>
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                        {clientsData.map((client, idx) => (
                            <Box key={client.name + idx}>
                                <Typography sx={{
                                    fontSize: '12px',
                                    fontWeight: 700,
                                    color: NAVY,
                                    mb: 0.5,
                                }}>
                                    {client.name}
                                </Typography>
                                <RiskBar
                                    counts={client.counts}
                                    rubrics={rubrics}
                                />
                            </Box>
                        ))}
                    </Box>
                </Box>

            </Box>


        </Box>
    );
};

export default ByCountryTab;
